import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import SignatureCanvas from "react-signature-canvas";
import {Checkbox, Upload} from "antd";
import "./signature.css";
import {CameraOutlined} from "@ant-design/icons";

export const DeliverySignatureComponent = ({
    title = "חתימת בית העסק",
    description = "בעת חתימתי זו, אני מאשר/ת כי עברתי על תוכן ההזמנה וכי כלל הפריטים המצוינים בה נמסרו במלואם ובמצב תקין.",
    onChange,
    showPreview = false,
    allowImageUpload = false,
    uploadToggleLabel = "לקוח לא זמין - השארתי בתיבת המסירה",
    showClearButton = true
}) => {
    const signaturePadRef = useRef(null);
    const uploadPreviewUrlRef = useRef(null);
    const [isEmpty, setIsEmpty] = useState(true);
    const [previewDataUrl, setPreviewDataUrl] = useState(null);
    const [useImageUpload, setUseImageUpload] = useState(false);

    const resetUploadPreview = useCallback(() => {
        if (uploadPreviewUrlRef.current) {
            URL.revokeObjectURL(uploadPreviewUrlRef.current);
            uploadPreviewUrlRef.current = null;
        }
        setPreviewDataUrl(null);
        setIsEmpty(true);
    }, []);

    const updateSignatureValue = useCallback(() => {
        if (useImageUpload) {
            return;
        }

        const signaturePad = signaturePadRef.current;
        if (!signaturePad) {
            return;
        }

        const empty = signaturePad.isEmpty();
        setIsEmpty(empty);

        if (empty) {
            setPreviewDataUrl(null);
            onChange?.(null);
            return;
        }

        signaturePad.getCanvas().toBlob(blob => {
            if (!blob) {
                return;
            }

            const timestamp = Date.now();
            const file = new File([blob], `signature-${timestamp}.png`, {type: "image/png"});
            const previewUrl = signaturePad.getCanvas().toDataURL("image/png");
            setPreviewDataUrl(previewUrl);
            onChange?.(file, false);
        }, "image/png");
    }, [onChange, useImageUpload]);

    const handleClear = useCallback(() => {
        if (useImageUpload) {
            resetUploadPreview();
            onChange?.(null);
            return;
        }

        signaturePadRef.current?.clear();
        setIsEmpty(true);
        setPreviewDataUrl(null);
        onChange?.(null);
    }, [onChange, resetUploadPreview, useImageUpload]);

    const handleUploadToggle = useCallback(
        ({target: {checked}}) => {
            setUseImageUpload(checked);
            signaturePadRef.current?.clear();
            resetUploadPreview();
            onChange?.(null);
        },
        [onChange, resetUploadPreview]
    );

    const handleFileSelected = useCallback(
        file => {
            if (!file) {
                return false;
            }

            resetUploadPreview();
            const objectUrl = URL.createObjectURL(file);
            uploadPreviewUrlRef.current = objectUrl;
            setPreviewDataUrl(objectUrl);
            setIsEmpty(false);
            onChange?.(file, true);

            return false;
        },
        [onChange, resetUploadPreview]
    );

    useEffect(() => {
        return () => {
            if (uploadPreviewUrlRef.current) {
                URL.revokeObjectURL(uploadPreviewUrlRef.current);
                uploadPreviewUrlRef.current = null;
            }
        };
    }, []);

    const preview = useMemo(() => {
        if (!previewDataUrl) {
            return null;
        }

        return (
            <div className="delivery-content-signature-preview">
                <img src={previewDataUrl} alt="signature preview" />
            </div>
        );
    }, [previewDataUrl]);

    return (
        <div className="delivery-content-confirmation-signature">
            <div className="delivery-content-confirmation-signature-title">{title}</div>
            {description && <div className="delivery-content-confirmation-signature-text">{description}</div>}

            {allowImageUpload && (
                <div className="delivery-content-signature-toggle">
                    <Checkbox checked={useImageUpload} onChange={handleUploadToggle}>
                        {uploadToggleLabel}
                    </Checkbox>
                </div>
            )}

            <div className="delivery-content-confirmation-signature-pad">
                {useImageUpload ? (
                    <Upload
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={handleFileSelected}
                        className="delivery-content-signature-upload">
                        {previewDataUrl ? (
                            <img src={previewDataUrl} alt="signature upload" />
                        ) : (
                            <span className="delivery-content-signature-upload-placeholder">
                                <CameraOutlined style={{fontSize: 20, marginLeft: 10}} />
                                הוספת תמונה
                            </span>
                        )}
                    </Upload>
                ) : (
                    <SignatureCanvas
                        ref={signaturePadRef}
                        penColor="#101010"
                        backgroundColor="transparent"
                        onEnd={updateSignatureValue}
                        canvasProps={{className: "delivery-content-signature-canvas"}}
                    />
                )}
            </div>

            {showClearButton && (
                <div className="delivery-content-signature-actions">
                    <button
                        type="button"
                        className="delivery-content-signature-clear"
                        onClick={handleClear}
                        disabled={isEmpty}>
                        {useImageUpload ? "נקה תמונה" : "נקה חתימה"}
                    </button>
                </div>
            )}
            {showPreview && preview}
        </div>
    );
};

export default DeliverySignatureComponent;
